import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, Thumbs, Zoom } from "swiper/modules";
import classNames from "classnames";
import IconaVideo from "../../icons/icona-video.svg";
import BadgePDP from "../modules/badge-pdp";
import { eventPdpSliderClick, eventPdpSliderSlide } from "../../utility/data-layer";
import TapToZoom from "./tap-to-zoom";
import { onetrustShowSettings } from "../../utility/onetrust";
import Icona3D from "../../icons/icona-3D.svg";

const GROUPS = ["C0003"];
const areCookiesEnabled = () =>
  window.OnetrustActiveGroups && GROUPS.some((item) => window.OnetrustActiveGroups.includes(item));

const SlideImg = ({ image, className = "slider-img-prod__img", alt = "", objectFit = "cover" }) => {
  return typeof image === "string" ? (
    <img src={image} alt={alt} className={className} />
  ) : (
    <GatsbyImage image={getImage(image)} alt={alt} className={className} objectFit={objectFit} />
  );
};

const SwiperPrevBtn = forwardRef(({ swiper, className, onClick }, ref) => {
  return (
    <button
      ref={ref}
      className={classNames(`swiper-button-prev ${className}`, {
        "d-none": !swiper,
        "swiper-button-lock": swiper?.isLocked,
        "swiper-button-disabled": swiper?.activeIndex === 0,
      })}
      onClick={() => {
        swiper?.slidePrev();
        onClick?.();
      }}
      aria-label="prev"
    ></button>
  );
});

const SwiperNextBtn = forwardRef(({ swiper, className, onClick, disabled }, ref) => {
  return (
    <button
      ref={ref}
      className={classNames(`swiper-button-next ${className}`, {
        "d-none": !swiper,
        "swiper-button-lock": swiper?.isLocked,
        "swiper-button-disabled": disabled,
      })}
      onClick={() => {
        swiper?.slideNext();
        onClick?.();
      }}
      aria-label="next"
    ></button>
  );
});

const GalleryProduct = ({
  slides,
  badge,
  enhanced,
  labelManagerLabels,
  showZoom,
  isModalGallery,
  zoomIdx,
  modalTab,
  pagePath,
  children,
}) => {
  const prevRef = useRef();
  const nextRef = useRef();
  const [swiperData, setSwiperData] = useState(null);
  const prevThumbRef = useRef();
  const nextThumbRef = useRef();

  const onClickThumb = useCallback(
    (e) => {
      e.stopPropagation();
      const next = parseInt(e.currentTarget.name);
      if (!swiperData) {
        return;
      }
      swiperData.slideTo(next);

      eventPdpSliderClick({
        type: isModalGallery ? "modal-thumb" : "thumb",
        slide: next,
        enhanced,
        typeImage: slides[swiperData?.activeIndex]?.s3Images
          ? "enhanced"
          : slides[swiperData?.activeIndex]?.dimensions
          ? "dimensions"
          : slides[swiperData?.activeIndex]?.youtubeId
          ? "video"
          : "classic",
      });
    },
    [enhanced, swiperData, isModalGallery, slides]
  );

  const onClickGallery = useCallback(
    (e) => {
      e.stopPropagation();
      const next = parseInt(e.currentTarget.name);
      if (!swiperData) {
        return;
      }

      swiperData.slideTo(next);
      eventPdpSliderClick({
        type: isModalGallery ? "modal-gallery" : "gallery",
        slide: next,
        enhanced,
        typeImage: slides[swiperData?.activeIndex]?.s3Images
          ? "enhanced"
          : slides[swiperData?.activeIndex]?.dimensions
          ? "dimensions"
          : slides[swiperData?.activeIndex]?.youtubeId
          ? "video"
          : "classic",
      });
    },
    [enhanced, swiperData, isModalGallery, slides]
  );

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [zoomActive, setZoomActive] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [dragData, setDragData] = useState(null);
  const [activeSlide, setActiveSlide] = useState(isModalGallery ? zoomIdx : 0);
  const [showAlert, setShowAlert] = useState(false);
  const [isEndSlider, setIsEndSlider] = useState(false);
  const [isEndSliderMain, setIsEndSliderMain] = useState(false);

  useEffect(() => {
    if (zoomIdx >= 0 && isModalGallery && swiperData) {
      swiperData.slideTo(zoomIdx);
    }
  }, [zoomIdx, isModalGallery, swiperData]);

  useEffect(() => {
    if (isModalGallery && swiperData) {
      setZoomActive(false);
      swiperData?.zoom?.out?.();
    }
  }, [isModalGallery, modalTab, swiperData]);

  useEffect(() => {
    if (window.OneTrust && !areCookiesEnabled()) {
      setShowAlert(true);
      window.OneTrust.OnConsentChanged(() => {
        setShowAlert(!areCookiesEnabled());
      });
      return () => setShowAlert(false);
    }
  }, []);

  const [showTapToZoom, setShowTapToZoom] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setShowTapToZoom(false);
    }, 2000);
  }, []);

  return (
    <>
      <div
        className={classNames(
          "slider-wrapper",
          isModalGallery && "slider-wrapper--gallery",
          badge?.length > 0 && "slider-wrapper--badge"
        )}
      >
        <div
          className={classNames(
            "slider-img--relative",
            isModalGallery && "slider-img--relative-gallery"
          )}
        >
          {badge?.length > 0 && !isModalGallery ? (
            <BadgePDP className="badge-pdp-desktop" data={badge[0]} pagePath={pagePath} />
          ) : null}
          <SwiperPrevBtn swiper={swiperData} ref={prevRef} className="slider-main-prev" />
          <Swiper
            spaceBetween={10}
            zoom={{ enabled: true, toggle: false, maxRatio: 1.7 }}
            modules={[Zoom, Navigation, Thumbs, Pagination]}
            className={classNames("slider-main", {
              "slider-main--zoom-in": isModalGallery && !zoomActive,
              "slider-main--zoom-out": isModalGallery && zoomActive,
              "slider-main--drag": isModalGallery && dragActive && zoomActive,
            })}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            pagination={
              !isModalGallery
                ? {
                    type: "fraction",
                  }
                : false
            }
            touchRatio={1}
            onTouchStart={(_swiper, e) => {
              const timeoutId = setTimeout(() => setDragActive(true), 300);
              const touchEvent = e?.changedTouches?.[0] ? e?.changedTouches?.[0] : e;
              setDragData({
                timeoutId,
                time: Date.now(),
                x: touchEvent.screenX,
                y: touchEvent.screenY,
              });
            }}
            onTouchEnd={(_swiper, e) => {
              clearTimeout(dragData?.timeoutId);
              setDragActive(false);
              const touchEvent = e?.changedTouches?.[0] ? e?.changedTouches?.[0] : e;
              const sqds =
                Math.pow(touchEvent?.screenX - dragData?.x, 2) +
                Math.pow(touchEvent?.screenY - dragData?.y, 2);
              setDragData(null);
              if (Date.now() - dragData?.time < 300 && sqds < 100) {
                if (zoomActive) {
                  setZoomActive(false);
                  swiperData.zoom.out();
                } else if (
                  isModalGallery &&
                  !slides[swiperData?.activeIndex]?.youtubeId &&
                  !slides[swiperData?.activeIndex]?.url360
                ) {
                  setZoomActive(true);
                  swiperData.zoom.in();
                }
              }
            }}
            breakpoints={{
              991: {
                touchRatio: 0,
              },
            }}
            onProgress={(swiper) => {
              setIsEndSliderMain(swiper?.isEnd);
            }}
            onSwiper={(swiper) => setSwiperData(swiper)}
            onActiveIndexChange={(swiper) => {
              if (swiper.activeIndex !== swiper.previousIndex) {
                eventPdpSliderSlide({
                  type: isModalGallery ? "modal-gallery" : "gallery",
                  slide: swiper.activeIndex,
                  enhanced,
                  typeImage: slides[swiper?.activeIndex]?.s3Images
                    ? "enhanced"
                    : slides[swiper?.activeIndex]?.dimensions
                    ? "dimensions"
                    : slides[swiper?.activeIndex]?.youtubeId
                    ? "video"
                    : "classic",
                });
              }
              setActiveSlide(swiper?.activeIndex);
              setZoomActive(false);
              if (
                thumbsSwiper &&
                !thumbsSwiper.slides[
                  swiper.activeIndex +
                    (swiper.activeIndex > swiper.previousIndex
                      ? +1
                      : swiper.activeIndex < swiper.previousIndex
                      ? -1
                      : 0)
                ]?.classList?.contains("swiper-slide-visible")
              ) {
                if (swiper.activeIndex > swiper.previousIndex) {
                  const visibleSlides = thumbsSwiper?.slides.filter((slide) =>
                    slide?.classList?.contains("swiper-slide-fully-visible")
                  )?.length;
                  const slideIndex = swiper.activeIndex - visibleSlides + 2;

                  if (slideIndex >= 0) {
                    thumbsSwiper.slideTo(slideIndex);
                  }
                } else if (swiper.activeIndex < swiper.previousIndex) {
                  thumbsSwiper.slidePrev();
                }
              }
            }}
            key={modalTab}
          >
            {slides &&
              slides.filter(Boolean).map((slide, i) => {
                return (
                  <SwiperSlide key={`main-slider-${i}-${isModalGallery ? "gallery" : ""}`}>
                    {slide?.url360 && !isModalGallery ? (
                      <button
                        className="swiper-zoom-container slider-img-prod__slide"
                        name={i}
                        onClick={(e) => {
                          if (!isModalGallery) {
                            showZoom(e);
                          }
                          onClickGallery(e);
                        }}
                        onMouseEnter={() => setShowTapToZoom(false)}
                      >
                        <div
                          className={classNames(
                            "swiper-zoom-target",
                            isModalGallery && zoomActive && "swiper-zoom-target--custom"
                          )}
                        >
                          <SlideImg
                            image={isModalGallery ? slide.full : slide.medium}
                            alt="3D"
                            className="slider-img-prod__img"
                            objectFit={isModalGallery ? "contain" : "cover"}
                          />
                          <Icona3D className="icon-svg" />
                        </div>
                      </button>
                    ) : slide?.url360 && isModalGallery ? (
                      <div className="slider-main__video-container">
                        <div className="slider-main__video-wrapper">
                          <iframe
                            src={slide.url360}
                            className="slider-main__video"
                            width="100%"
                            title={labelManagerLabels.video}
                            allowFullScreen={true}
                            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                            referrerPolicy="strict-origin-when-cross-origin"
                          />
                        </div>
                      </div>
                    ) : slide?.youtubeId ? (
                      <div className="slider-main__video-container">
                        {showAlert ? (
                          <div className="slider-main__video-wrapper">
                            <div className="slider-main__video-button-container">
                              <div className="slider-main__video-button-wrapper">
                                {labelManagerLabels.cookieGalleryButton}
                                <button
                                  className="button primary blue slider-main__video-button"
                                  onClick={onetrustShowSettings}
                                >
                                  {labelManagerLabels.cookieGalleryButtonManage}
                                </button>
                              </div>
                            </div>
                            <iframe
                              src={`https://www.youtube-nocookie.com/embed/${slide.youtubeId}`}
                              className="slider-main__video"
                              width="100%"
                              title={labelManagerLabels.video}
                              allowFullScreen={true}
                              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                              referrerPolicy="strict-origin-when-cross-origin"
                            />
                          </div>
                        ) : (
                          <div className="slider-main__video-wrapper">
                            <iframe
                              src={`https://www.youtube.com/embed/${slide.youtubeId}?rel=0&enablejsapi=1&version=3?rel=0`}
                              className="slider-main__video"
                              width="100%"
                              title={labelManagerLabels.video}
                              allowFullScreen={true}
                              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                              referrerPolicy="strict-origin-when-cross-origin"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        className="swiper-zoom-container"
                        name={i}
                        onClick={(e) => {
                          if (!isModalGallery) {
                            showZoom(e);
                          }
                          onClickGallery(e);
                        }}
                        onMouseEnter={() => setShowTapToZoom(false)}
                      >
                        <div
                          className={classNames(
                            "swiper-zoom-target",
                            isModalGallery && zoomActive && "swiper-zoom-target--custom"
                          )}
                        >
                          <SlideImg
                            image={isModalGallery ? slide.full : slide.medium}
                            alt={slide.title + " " + i}
                            className={
                              isModalGallery ? "slider-zoom-img-prod__img" : "slider-img-prod__img"
                            }
                            objectFit={isModalGallery ? "contain" : "cover"}
                          />
                        </div>
                        {isModalGallery && <TapToZoom showTapToZoom={showTapToZoom} />}
                      </button>
                    )}
                  </SwiperSlide>
                );
              })}
          </Swiper>
          <SwiperNextBtn
            swiper={swiperData}
            ref={nextRef}
            className="slider-main-next"
            disabled={isEndSliderMain}
          />
        </div>
        <div
          className={classNames(
            "slider-thumbs-gallery",
            zoomActive && isModalGallery && "slider-thumbs-gallery--zoom"
          )}
        >
          {!!children && <div>{children}</div>}
          <div className="slider-thumbs-wrapper">
            <SwiperPrevBtn
              swiper={thumbsSwiper}
              ref={prevThumbRef}
              onClick={() => {
                if (
                  !thumbsSwiper.slides[swiperData.activeIndex]?.classList?.contains(
                    "swiper-slide-fully-visible"
                  )
                ) {
                  const slideIndex =
                    thumbsSwiper?.slides?.length -
                    1 -
                    [...thumbsSwiper?.slides]
                      ?.reverse()
                      ?.findIndex(
                        (slide, index) =>
                          index > 0 && slide?.classList?.contains("swiper-slide-fully-visible")
                      );

                  if (slideIndex >= 0) {
                    swiperData.slideTo(slideIndex);
                  }
                }
              }}
              className="slider-thumbs-prev"
            />
            <Swiper
              spaceBetween={16}
              slidesPerView={"auto"}
              watchSlidesProgress={true}
              scrollbar={{ draggable: false }}
              modules={[Navigation, Thumbs, Scrollbar]}
              className={classNames("slider-thumbs", isEndSlider && "slider-thumbs--end")}
              navigation={{
                prevEl: prevThumbRef.current,
                nextEl: nextThumbRef.current,
              }}
              onInit={(swiper) => {
                swiper.navigation.prevEl = prevThumbRef.current;
                swiper.navigation.nextEl = nextThumbRef.current;
                swiper.navigation.update();
              }}
              breakpoints={{
                991: {
                  scrollbar: {
                    draggable: true,
                  },
                },
              }}
              onProgress={(swiper) => {
                setIsEndSlider(swiper?.isEnd);
              }}
              onSwiper={(swiper) => setThumbsSwiper(swiper)}
              onActiveIndexChange={(swiper) => {
                if (swiper.activeIndex !== swiper.previousIndex) {
                  eventPdpSliderSlide({
                    type: isModalGallery ? "modal-thumb" : "thumb",
                    slide: swiper.activeIndex,
                    enhanced,
                    typeImage: slides[swiper?.activeIndex]?.s3Images
                      ? "enhanced"
                      : slides[swiper?.activeIndex]?.dimensions
                      ? "dimensions"
                      : slides[swiper?.activeIndex]?.youtubeId
                      ? "video"
                      : "classic",
                  });
                }
              }}
            >
              {slides &&
                slides.filter(Boolean).map((slide, i) => {
                  return (
                    <SwiperSlide key={i}>
                      {slide?.url360 ? (
                        <button
                          className={classNames(activeSlide === i && "swiper-slide-thumb-active")}
                          href={slide.url360}
                          aria-label="3D"
                          name={i}
                          onClick={onClickThumb}
                        >
                          <SlideImg
                            image={slide.thumb}
                            alt="3D"
                            className="slider-thumbs-img-prod__img"
                          />
                          <Icona3D className="icon-svg" />
                        </button>
                      ) : slide?.youtubeId ? (
                        <button
                          className={classNames(activeSlide === i && "swiper-slide-thumb-active")}
                          name={i}
                          aria-label={labelManagerLabels.video}
                          onClick={onClickThumb}
                        >
                          <SlideImg
                            image={slide.thumb}
                            alt="youtube"
                            className="slider-thumbs-img-prod__img slider-thumbs-img-prod__img--video"
                          />
                          <IconaVideo className="slider-thumbs-img-prod__img--video-icon" />
                        </button>
                      ) : (
                        <button
                          className={classNames(activeSlide === i && "swiper-slide-thumb-active")}
                          key={`thumbs-slider-${i}`}
                          name={i}
                          onClick={onClickThumb}
                        >
                          <SlideImg
                            image={slide.thumb}
                            alt={slide.title}
                            className="slider-thumbs-img-prod__img"
                          />
                        </button>
                      )}
                    </SwiperSlide>
                  );
                })}
            </Swiper>
            <SwiperNextBtn
              swiper={thumbsSwiper}
              ref={nextThumbRef}
              onClick={() => {
                if (
                  !thumbsSwiper.slides[swiperData.activeIndex]?.classList?.contains(
                    "swiper-slide-fully-visible"
                  )
                ) {
                  const slideIndex = thumbsSwiper?.slides?.findIndex(
                    (slide, index) =>
                      index > thumbsSwiper.previousIndex &&
                      slide?.classList?.contains("swiper-slide-fully-visible")
                  );
                  if (slideIndex >= 0) {
                    swiperData.slideTo(slideIndex);
                  }
                }
              }}
              className="slider-thumbs-next"
              disabled={isEndSlider}
            />
          </div>
        </div>
      </div>
      {badge?.length > 0 && !isModalGallery ? (
        <div className="badge-pdp-mobile__wrapper">
          <BadgePDP className="badge-pdp-mobile" data={badge[0]} pagePath={pagePath} />
        </div>
      ) : null}
    </>
  );
};

export default GalleryProduct;
